import { useState, useEffect, useCallback } from 'react';
import { NotificationService } from '@/services/notificationService';
import { useAuthState } from './useAuthState';
import { Notification } from '@/integrations/mysql/types';

export const useNotifications = () => {
  const { userId, isAuthenticated } = useAuthState();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !userId || typeof userId !== 'number') {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [notificationsData, unreadCountData] = await Promise.all([
        NotificationService.getNotifications(userId, 50),
        NotificationService.getUnreadCount(userId)
      ]);
      
      setNotifications(notificationsData);
      setUnreadCount(unreadCountData);
    } catch (err) {
      console.error('Erro ao buscar notificações:', err);
      setError('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userId]);

  const markAsRead = useCallback(async (notificationId: number) => {
    if (!userId) return;

    try {
      const success = await NotificationService.markAsRead(notificationId, userId);
      if (success) {
        // Atualizar estado local
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, is_read: true, read_at: new Date().toISOString() }
              : notification
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // Disparar evento para atualizar contadores em outros componentes
        window.dispatchEvent(new CustomEvent('notificationUpdated'));
      }
    } catch (err) {
      console.error('Erro ao marcar notificação como lida:', err);
    }
  }, [userId]);

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;

    try {
      const success = await NotificationService.markAllAsRead(userId);
      if (success) {
        // Atualizar estado local
        setNotifications(prev => 
          prev.map(notification => ({
            ...notification,
            is_read: true,
            read_at: new Date().toISOString()
          }))
        );
        setUnreadCount(0);
        
        // Disparar evento para atualizar contadores em outros componentes
        window.dispatchEvent(new CustomEvent('notificationUpdated'));
      }
    } catch (err) {
      console.error('Erro ao marcar todas as notificações como lidas:', err);
    }
  }, [userId]);

  const deleteNotification = useCallback(async (notificationId: number) => {
    if (!userId) return;

    try {
      const success = await NotificationService.deleteNotification(notificationId, userId);
      if (success) {
        // Verificar se era uma notificação não lida antes de deletar
        const notification = notifications.find(n => n.id === notificationId);
        const wasUnread = notification && !notification.is_read;
        
        // Atualizar estado local
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        
        // Disparar evento para atualizar contadores em outros componentes
        window.dispatchEvent(new CustomEvent('notificationUpdated'));
      }
    } catch (err) {
      console.error('Erro ao deletar notificação:', err);
    }
  }, [userId, notifications]);

  // Buscar notificações quando o usuário fizer login
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Atualizar notificações a cada 30 segundos
  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, isAuthenticated, userId]);

  // Listener para eventos de atualização de notificações
  useEffect(() => {
    const handleNotificationUpdate = () => {
      console.log('🔔 Evento de atualização de notificação recebido');
      fetchNotifications();
    };

    window.addEventListener('notificationUpdated', handleNotificationUpdate);
    
    return () => {
      window.removeEventListener('notificationUpdated', handleNotificationUpdate);
    };
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    hasUnreadNotifications: unreadCount > 0
  };
};
